import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Card, GameState, GamePhase, Player, PlayerRoundResult } from '../types';
import { useOnlineGame } from '../hooks/useOnlineGame';
import { calculateOnlineRoundScores, declareLowHand, declareThreeAces, MatchData, sendEmote, sendMessage } from '../services/matchmaking';
import { cn } from '../lib/utils';
import GameBoard from './GameBoard';

interface OnlineGameViewProps {
  matchId: string;
  myUid: string;
  onBack: () => void;
}

export default function OnlineGameView({ matchId, myUid, onBack }: OnlineGameViewProps) {
  const {
    match,
    loading,
    error,
    isMyTurn,
    myPlayerIndex,
    playCard,
    selectCaptureOption,
    possibleCaptures,
    opponentDisconnected,
    clearDisconnect,
    isConnected,
    startNextRound,
    cancelSelection
  } = useOnlineGame(matchId, myUid);

  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 390);

  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ playerIndex: number; text: string; timestamp: number }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const [activeEmote, setActiveEmote] = useState<{
    playerIndex: number;
    emote: 'angry' | 'cool' | 'shocked' | 'handshake' | 'lol';
    timestamp: number;
  } | null>(null);

  const EMOTE_MAP = {
    angry: '😠',
    cool: '😎',
    shocked: '😲',
    handshake: '🤝',
    lol: '😂'
  };

  // Positions array is ordered clockwise from local player's position:
  // positions[0] = where local player sits, positions[1] = next clockwise, etc.
  // Base layouts (before rotation): 2P=[bottom,top], 3P=[bottom,left-center,top],
  // 4P=[bottom,right,top,left]
  const getEmotePositionClass = (relIndex: number, myPlayerIndex: number, numPlayers: number) => {
    let basePositions: string[];
    if (numPlayers === 2) {
      // [bottom, top]
      basePositions = [
        'bottom-28 left-1/2 -translate-x-1/2',
        'top-28 left-1/2 -translate-x-1/2',
      ];
    } else if (numPlayers === 3) {
      // [bottom, left-center, top]
      basePositions = [
        'bottom-28 left-1/2 -translate-x-1/2',
        'left-28 top-1/2 -translate-y-1/2',
        'top-28 left-1/2 -translate-x-1/2',
      ];
    } else {
      // [bottom, right, top, left]
      basePositions = [
        'bottom-28 left-1/2 -translate-x-1/2',
        'right-28 top-1/2 -translate-y-1/2',
        'top-28 left-1/2 -translate-x-1/2',
        'left-28 top-1/2 -translate-y-1/2',
      ];
    }
    // Rotate so basePositions[0] aligns with where the local player actually sits
    const rotated = [...basePositions.slice(myPlayerIndex), ...basePositions.slice(0, myPlayerIndex)];
    return rotated[relIndex];
  };

  // States for animations
  const [scopaEvent, setScopaEvent] = useState<{ playerIndex: number; timestamp: number } | null>(null);
  const [lowHandEvent, setLowHandEvent] = useState<{ playerIndex: number; timestamp: number } | null>(null);
  const [dealId, setDealId] = useState(1);

  // Stable seed cache for scopa particles
  const scopaParticleSeeds = useMemo(() =>
    Array.from({ length: 40 }, () => ({
      x: (Math.random() - 0.5) * 0.8,
      y: (Math.random() - 0.5) * 0.8,
      scale: Math.random() * 0.5 + 0.5,
      rotate: Math.random() * 360,
      duration: 1.5 + Math.random()
    })), []
  );

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (showChat && chatMessages.length > 0) {
      setTimeout(() => {
        chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [chatMessages, showChat]);

  // Sync chat messages via Firebase — subscribe to match.lastMessage
  const lastProcessedMsgRef = useRef<number>(0);
  useEffect(() => {
    if (!match?.lastMessage) return;
    const msg = match.lastMessage;
    // Only add if strictly newer than what we've already processed
    if (msg.timestamp > lastProcessedMsgRef.current) {
      lastProcessedMsgRef.current = msg.timestamp;
      setChatMessages(prev => {
        // Avoid duplicates (optimistic local add + Firebase echo could double-add)
        if (prev.some(m => m.timestamp === msg.timestamp && m.playerIndex === msg.playerIndex)) {
          return prev;
        }
        return [...prev, { playerIndex: msg.playerIndex, text: msg.text, timestamp: msg.timestamp }];
      });
    }
  }, [match?.lastMessage]);

  // Track state transitions to trigger animations and deal resets
  const prevMatchRef = useRef<MatchData | null>(null);
  const prevDeckLengthRef = useRef(40);
  const prevStatusRef = useRef<string | undefined>('playing');
  const [localCapturingCards, setLocalCapturingCards] = useState<{ cards: Card[]; playedCard: Card; playerIndex: number } | null>(null);
  const [localLastPlayedCard, setLocalLastPlayedCard] = useState<{ card: Card; playerIndex: number } | null>(null);
  const capturingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const captureDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPlayedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [localTable, setLocalTable] = useState<Card[]>([]);

  // Cards that should be shown face-up to me (opponent's sbarazzo reveal)
  const revealedCardIds = useMemo(() => {
    if (!match || myPlayerIndex === -1) return new Set<string>();
    const otherRevealed = match.revealedHands?.flatMap((hand, idx) => idx !== myPlayerIndex ? hand : []) || [];
    return new Set(otherRevealed);
  }, [match?.revealedHands, myPlayerIndex]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!match || myPlayerIndex === -1) return;

    if (!prevMatchRef.current) {
      // First load: initialize table state immediately
      setLocalTable(match.table || []);
    } else {
      const prev = prevMatchRef.current;
      const numPlayers = match.numPlayers;

      // Check for scopa and declaration animations
      for (let originalIndex = 0; originalIndex < numPlayers; originalIndex++) {
        const relativeIndex = (originalIndex - myPlayerIndex + numPlayers) % numPlayers;

        const prevScope = prev.scope?.[originalIndex] ?? 0;
        const currentScope = match.scope?.[originalIndex] ?? 0;
        if (currentScope > prevScope) {
          setScopaEvent({ playerIndex: relativeIndex, timestamp: Date.now() });
        }

        const prevLowHand = prev.players?.[originalIndex]?.hasDeclaredLowHand || false;
        const currentLowHand = match.players?.[originalIndex]?.hasDeclaredLowHand || false;
        if (currentLowHand && !prevLowHand) {
          setLowHandEvent({ playerIndex: relativeIndex, timestamp: Date.now() });
        }
      }

      // Check for capture animations (diffing previous and current table states)
      const prevLastPlayed = prev.lastPlayedCard;
      const currentLastPlayed = match.lastPlayedCard;
      const isNewPlay = currentLastPlayed && (!prevLastPlayed || prevLastPlayed.card.id !== currentLastPlayed.card.id);

      if (isNewPlay) {
        const prevTable = prev.table || [];
        const currentTable = match.table || [];
        const playedCard = currentLastPlayed.card;
        const originalPlayerIndex = currentLastPlayed.playerIndex;
        const relativePlayerIndex = (originalPlayerIndex - myPlayerIndex + numPlayers) % numPlayers;

        // Stop and clean up any ongoing capture animation from a previous turn
        if (captureDelayTimeoutRef.current || capturingTimeoutRef.current) {
          if (captureDelayTimeoutRef.current) {
            clearTimeout(captureDelayTimeoutRef.current);
            captureDelayTimeoutRef.current = null;
          }
          if (capturingTimeoutRef.current) {
            clearTimeout(capturingTimeoutRef.current);
            capturingTimeoutRef.current = null;
          }
          setLocalCapturingCards(null);
          // Instantly sync to the previous table state (removing old captured cards from screen)
          setLocalTable(prevTable);
        }

        // Set local last played card for rendering card in center with indicator
        setLocalLastPlayedCard({
          card: playedCard,
          playerIndex: relativePlayerIndex
        });

        // Set a timeout to clear local last played card after 1800ms
        if (lastPlayedTimeoutRef.current) {
          clearTimeout(lastPlayedTimeoutRef.current);
        }
        lastPlayedTimeoutRef.current = setTimeout(() => {
          setLocalLastPlayedCard(null);
          lastPlayedTimeoutRef.current = null;
        }, 1800);

        const captured = prevTable.filter(
          (prevCard) => !currentTable.some((currentCard) => currentCard.id === prevCard.id)
        );

        if (captured.length > 0 || (playedCard.value === 1 && prevTable.length === 0)) {
          // Phase 1: Wait 800ms for the played card to finish throwing/animating onto the table
          captureDelayTimeoutRef.current = setTimeout(() => {
            setLocalCapturingCards({
              cards: captured,
              playedCard,
              playerIndex: relativePlayerIndex
            });
            captureDelayTimeoutRef.current = null;

            // Phase 2: Run the actual flying/gathering animation (850ms duration)
            capturingTimeoutRef.current = setTimeout(() => {
              setLocalCapturingCards(null);
              setLocalTable(currentTable); // Update table state to hide captured cards ONLY after flight finishes!
              setLocalLastPlayedCard(null); // Clear played card indicator immediately after capture finishes
              if (lastPlayedTimeoutRef.current) {
                clearTimeout(lastPlayedTimeoutRef.current);
                lastPlayedTimeoutRef.current = null;
              }
              capturingTimeoutRef.current = null;
            }, 850);
          }, 800);
        } else {
          // No capture: update local table immediately
          setLocalTable(currentTable);
        }
      } else {
        // If lastPlayedCard was cleared in the database (e.g. round start), sync local state
        if (!currentLastPlayed) {
          setLocalLastPlayedCard(null);
          if (lastPlayedTimeoutRef.current) {
            clearTimeout(lastPlayedTimeoutRef.current);
            lastPlayedTimeoutRef.current = null;
          }
        }

        // Not a new card play: only sync table immediately if there is no active/pending capture animation
        const isAnimationPending = captureDelayTimeoutRef.current !== null || capturingTimeoutRef.current !== null;
        if (!isAnimationPending) {
          setLocalTable(match.table || []);
        }
      }
    }

    // Trigger deal animation on state / deck updates
    const deckLength = match.deck?.length ?? 0;
    const status = match.status;
    if (deckLength !== prevDeckLengthRef.current || status !== prevStatusRef.current) {
      setDealId(d => d + 1);
    }

    prevMatchRef.current = match;
    prevDeckLengthRef.current = deckLength;
    prevStatusRef.current = status;
  }, [match, myPlayerIndex]);

  useEffect(() => {
    if (scopaEvent) {
      const t = setTimeout(() => setScopaEvent(null), 3000);
      return () => clearTimeout(t);
    }
  }, [scopaEvent]);

  useEffect(() => {
    if (lowHandEvent) {
      const t = setTimeout(() => setLowHandEvent(null), 3000);
      return () => clearTimeout(t);
    }
  }, [lowHandEvent]);

  useEffect(() => {
    if (match?.lastEmote) {
      setActiveEmote(match.lastEmote);
      const t = setTimeout(() => setActiveEmote(null), 2500);
      return () => clearTimeout(t);
    }
  }, [match?.lastEmote?.timestamp]);

  // Cleanup active capturing timers on unmount
  useEffect(() => {
    return () => {
      if (captureDelayTimeoutRef.current) {
        clearTimeout(captureDelayTimeoutRef.current);
      }
      if (capturingTimeoutRef.current) {
        clearTimeout(capturingTimeoutRef.current);
      }
      if (lastPlayedTimeoutRef.current) {
        clearTimeout(lastPlayedTimeoutRef.current);
      }
    };
  }, []);

  // Sync capture modal visible state with hook possible captures
  useEffect(() => {
    if (possibleCaptures.length > 1) {
      setShowCaptureModal(true);
    } else {
      setShowCaptureModal(false);
    }
  }, [possibleCaptures]);

  const handleCardClick = async (card: Card) => {
    if (!isMyTurn || isPlaying) return;
    if (!isConnected) return;
    setIsPlaying(true);
    setSelectedCard(card);

    try {
      await playCard(card);
    } catch (err) {
      console.error('Errore durante la giocata:', err);
      setSelectedCard(null);
    } finally {
      setIsPlaying(false);
    }
  };

  const handleCaptureSelect = async (captureOption: Card[]) => {
    if (!selectedCard) return;
    if (!isConnected) return;
    setShowCaptureModal(false);
    setIsPlaying(true);

    try {
      await selectCaptureOption(captureOption);
      setSelectedCard(null);
    } catch (err) {
      console.error('Errore durante la selezione della cattura:', err);
    } finally {
      setIsPlaying(false);
    }
  };

  const handleCancelCapture = () => {
    setShowCaptureModal(false);
    setSelectedCard(null);
    cancelSelection();
  };

  const handleDeclareLowHand = async () => {
    if (!isMyTurn || isPlaying) return;
    if (!isConnected) return;
    setIsPlaying(true);
    try {
      await declareLowHand(matchId, myPlayerIndex, myUid);
    } catch (err) {
      console.error('Failed to declare low hand:', err);
    } finally {
      setIsPlaying(false);
    }
  };

  const handleDeclareThreeAces = async () => {
    if (!isMyTurn || isPlaying) return;
    if (!isConnected) return;
    setIsPlaying(true);
    try {
      await declareThreeAces(matchId, myPlayerIndex, myUid);
    } catch (err) {
      console.error('Failed to declare three aces:', err);
    } finally {
      setIsPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center online-loading-bg">
        <div className="text-white/60 text-lg font-bold">Caricamento...</div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 online-loading-bg">
        <div className="text-white/80 text-lg font-bold">{error || 'Partita non trovata'}</div>
        <button onClick={onBack} className="text-white/40 hover:text-white/70 text-sm uppercase tracking-widest transition-colors">
          ← Torna al menu
        </button>
      </div>
    );
  }

  // Map Firebase match data into the GameState shape GameBoard expects, overriding table and lastPlayedCard with our local state for animation timing
  const gameState = {
    ...matchDataToGameState(match, myUid, scopaEvent, lowHandEvent),
    table: localTable,
    lastPlayedCard: localLastPlayedCard
  };

  // Compute roundStats for RoundSummary rendering
  const roundStats = (match.status === 'roundEnd' || match.status === 'finished') ? (() => {
    const calculated = calculateOnlineRoundScores(match);
    const numPlayers = match.numPlayers;
    
    // Rotate the results to match our relative perspective
    const rotatedResults: PlayerRoundResult[] = [];
    for (let i = 0; i < numPlayers; i++) {
      const originalIndex = (myPlayerIndex + i) % numPlayers;
      const res = calculated.results[originalIndex];
      rotatedResults.push({
        cards: res?.cards ?? 0,
        coins: res?.coins ?? 0,
        settebello: res?.settebello ?? 0,
        dieciDenari: res?.dieciDenari ?? 0,
        dueDenari: res?.dueDenari ?? 0,
        primiera: res?.primiera ?? 0,
        lowHand: res?.lowHand ?? 0,
        threeAces: res?.threeAces ?? 0,
        scope: res?.scope ?? 0,
        score: res?.score ?? 0,
      });
    }

    return {
      pScore: rotatedResults[0]?.score ?? 0,
      aiScore: rotatedResults[1]?.score ?? 0, // Fallback for backward compatibility in standard components
      pResults: rotatedResults[0] ?? null,
      aiResults: rotatedResults[1] ?? null, // Fallback for backward compatibility
      results: rotatedResults,
    };
  })() : null;

  const onlineOverlay = (
    <>
      {/* Disconnect Overlay */}
      <AnimatePresence>
        {opponentDisconnected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto"
          >
            <div className="min-h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="text-6xl mb-4">📡</div>
              <h2 className="text-yellow-400 text-2xl font-bold mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                Problema di connessione
              </h2>
              <p className="text-white/60 text-sm mb-6 max-w-xs leading-relaxed">
                Un altro giocatore non ha effettuato mosse da oltre un minuto. Vuoi aspettare ancora o abbandonare la partita?
              </p>
              <div className="flex flex-col gap-3 w-full max-w-[200px]">
                <button
                  onClick={clearDisconnect}
                  className="w-full py-3 px-6 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 rounded-xl text-yellow-400 font-bold text-sm uppercase tracking-widest transition-all"
                >
                  🔄 Riprova connessione
                </button>
                <button
                  onClick={onBack}
                  className="w-full py-3 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white/70 font-medium text-sm uppercase tracking-widest transition-all"
                >
                  ← Abbandona
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Lost Overlay */}
      <AnimatePresence>
        {!isConnected && (match.status === 'playing' || match.status === 'roundEnd' || match.status === 'finished') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto"
          >
            <div className="min-h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="text-6xl mb-4">📡</div>
              <h2 className="text-yellow-400 text-2xl font-bold mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                Connessione persa
              </h2>
              <p className="text-white/60 text-sm mb-6 max-w-xs leading-relaxed">
                La connessione con il server è stata interrotta. Tentativo di riconnessione in corso...
              </p>
              <div className="flex flex-col gap-4 items-center w-full max-w-[240px]">
                <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                  Riconnessione...
                </div>
                <button
                  onClick={onBack}
                  className="w-full mt-4 py-3 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white/70 font-semibold text-xs uppercase tracking-widest transition-all"
                >
                  ← Abbandona Partita
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Emote + Chat Buttons */}
      {match.status === 'playing' && (
        <div className="fixed bottom-24 right-4 z-40 flex flex-col items-end gap-2">
          {/* Chat Panel */}
          <AnimatePresence>
            {showChat && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                className="glass-panel w-72 max-h-80 flex flex-col border border-white/10 bg-black/85 backdrop-blur-md rounded-2xl overflow-hidden shadow-2xl mb-2"
              >
                {/* Header */}
                <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
                  <span className="text-white/70 text-xs font-semibold uppercase tracking-widest">Chat</span>
                  <button
                    onClick={() => setShowChat(false)}
                    className="text-white/40 hover:text-white/70 text-lg leading-none transition-colors"
                  >
                    ×
                  </button>
                </div>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-52 scrollbar-thin">
                  {chatMessages.length === 0 && (
                    <p className="text-white/30 text-xs text-center italic">Nessun messaggio</p>
                  )}
                  {chatMessages.map((msg, i) => {
                    const isMe = msg.playerIndex === myPlayerIndex;
                    return (
                      <div key={i} className={cn('flex flex-col', isMe ? 'items-end' : 'items-start')}>
                        <span className={cn(
                          'px-3 py-1.5 rounded-2xl text-sm max-w-[85%] break-words',
                          isMe
                            ? 'bg-yellow-500/80 text-black rounded-br-sm'
                            : 'bg-white/10 text-white/90 rounded-bl-sm'
                        )}>
                          {msg.text}
                        </span>
                      </div>
                    );
                  })}
                  <div ref={chatMessagesEndRef} />
                </div>
                {/* Input */}
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const text = chatInput.trim();
                    if (!text || myPlayerIndex === -1) return;
                    setChatInput('');
                    // Send to Firebase — useEffect will add to chatMessages on echo
                    await sendMessage(matchId, myPlayerIndex, text);
                  }}
                  className="p-2 border-t border-white/10"
                >
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Scrivi..."
                      maxLength={200}
                      className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-yellow-500/60"
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim()}
                      className="px-3 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-30 disabled:cursor-not-allowed text-black rounded-xl text-sm font-semibold transition-colors"
                    >
                      ▲
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Buttons row */}
          <div className="flex gap-2">
            {/* Emote button */}
            <button
              onClick={() => { setShowEmotePicker(prev => !prev); setShowChat(false); }}
              className="w-12 h-12 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg shadow-yellow-500/20 border border-yellow-600/40 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
            >
              <span className="text-xl">😀</span>
            </button>

            {/* Chat button */}
            <button
              onClick={() => { setShowChat(prev => !prev); setShowEmotePicker(false); }}
              className={cn(
                "w-12 h-12 rounded-full text-black shadow-lg border flex items-center justify-center transition-all hover:scale-105 active:scale-95",
                showChat
                  ? "bg-white/90 border-white/40"
                  : "bg-white/80 border-white/20 hover:bg-white/90"
              )}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
              </svg>
              {/* Unread badge */}
              {chatMessages.length > 0 && !showChat && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                  {chatMessages.length > 9 ? '9+' : chatMessages.length}
                </span>
              )}
            </button>
          </div>

          {/* Emote Picker Popup */}
          <AnimatePresence>
            {showEmotePicker && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                className="absolute bottom-14 right-0 glass-panel p-2.5 rounded-2xl border border-white/10 flex gap-2 shadow-2xl bg-black/85 backdrop-blur-md"
              >
                {(Object.keys(EMOTE_MAP) as Array<keyof typeof EMOTE_MAP>).map((emo) => (
                  <button
                    key={emo}
                    onClick={async () => {
                      setShowEmotePicker(false);
                      if (myPlayerIndex !== -1) {
                        const timestamp = Date.now();
                        setActiveEmote({ playerIndex: myPlayerIndex, emote: emo, timestamp });
                        await sendEmote(matchId, myPlayerIndex, emo);
                      }
                    }}
                    className="w-10 h-10 rounded-xl hover:bg-white/10 flex items-center justify-center text-2xl transition-all hover:scale-110"
                  >
                    {EMOTE_MAP[emo]}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Emote Bubble overlay */}
      <AnimatePresence>
        {activeEmote && (
          <motion.div
            key={activeEmote.timestamp}
            className={cn(
              "absolute z-50 pointer-events-none",
              getEmotePositionClass(
                (activeEmote.playerIndex - myPlayerIndex + match.numPlayers) % match.numPlayers,
                myPlayerIndex,
                match.numPlayers
              )
            )}
            initial={{ opacity: 0, scale: 0.4, y: 20 }}
            animate={{ opacity: 1, scale: 1.1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{ type: 'spring', stiffness: 250, damping: 14 }}
          >
            <div className="relative bg-black/85 border border-white/20 px-4 py-2.5 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.65)] flex items-center justify-center text-3xl font-bold">
              {EMOTE_MAP[activeEmote.emote]}
              {/* Little speech bubble tail */}
              <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-black/85 border-r border-b border-white/20 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <GameBoard
      gameState={gameState}
      selectedCard={selectedCard}
      hoveredCardId={hoveredCardId}
      possibleCaptures={possibleCaptures}
      capturingCards={localCapturingCards}
      showRules={showRules}
      showCapturesMenu={showCaptureModal}
      scopaParticleSeeds={scopaParticleSeeds}
      errorMessage={null}
      roundStats={roundStats}
      dealId={dealId}
      revealedCardIds={revealedCardIds}
      revealingCardIds={new Set()}
      viewportWidth={viewportWidth}
      onCardSelect={handleCardClick}
      onCaptureSelect={handleCaptureSelect}
      onHoverCard={setHoveredCardId}
      onToggleRules={() => setShowRules(r => !r)}
      onDismissCapturesMenu={handleCancelCapture}
      onNextRound={startNextRound}
      onRestart={onBack}
      onExit={onBack}
      onDeclareLowHand={handleDeclareLowHand}
      onDeclareThreeAces={handleDeclareThreeAces}
      playerName={gameState.players[0].name}
      isOnline={true}
      isConnected={isConnected}
      onlineOverlay={onlineOverlay}
      targetScore={match?.targetScore}
    />
  );
}

// Adapter: convert MatchData (Firebase) to GameState (GameController/GameBoard)
export function matchDataToGameState(
  match: MatchData,
  myUid: string,
  scopaEvent: { playerIndex: number; timestamp: number } | null,
  lowHandEvent: { playerIndex: number; timestamp: number } | null
): GameState {
  const mySeatIndex = match.players.findIndex(p => p.uid === myUid);
  const activeSeatIndex = mySeatIndex !== -1 ? mySeatIndex : 0;
  const numPlayers = match.numPlayers;

  const players: Player[] = [];
  for (let i = 0; i < numPlayers; i++) {
    const originalIndex = (activeSeatIndex + i) % numPlayers;
    const p = match.players[originalIndex];
    players.push({
      id: i,
      name: p.uid === myUid ? `${p.name} (Tu)` : (p.name || `Giocatore ${originalIndex + 1}`),
      hand: p.hand || [],
      captured: p.captured || [],
      totalScore: p.totalScore ?? 0,
      scope: match.scope ? (match.scope[originalIndex] ?? 0) : 0,
      currentRoundScore: 0,
      hasDeclaredLowHand: p.hasDeclaredLowHand || false,
      hasDeclaredThreeAces: p.hasDeclaredThreeAces || false,
      lowHandCount: p.lowHandCount || 0,
      threeAcesCount: p.threeAcesCount || 0,
      readyForNextRound: p.readyForNextRound || false,
    });
  }

  // Adjust currentPlayerIndex: local player (index 0) and remote players (1, 2, 3)
  const currentPlayerIndex = (match.currentPlayerIndex - activeSeatIndex + numPlayers) % numPlayers;

  // Determine phase from match status
  let phase: GamePhase = 'playing';
  if (match.status === 'finished') {
    phase = 'gameOver';
  } else if (match.status === 'roundEnd') {
    phase = 'roundEnd';
  }

  // Compute round results when round has ended or game is finished
  let roundResults: PlayerRoundResult[] | undefined;
  if (phase === 'roundEnd' || phase === 'gameOver') {
    const calculated = calculateOnlineRoundScores(match);
    const rotatedResults: PlayerRoundResult[] = [];
    for (let i = 0; i < numPlayers; i++) {
      const originalIndex = (activeSeatIndex + i) % numPlayers;
      const res = calculated.results[originalIndex];
      rotatedResults.push({
        cards: res?.cards ?? 0,
        coins: res?.coins ?? 0,
        settebello: res?.settebello ?? 0,
        dieciDenari: res?.dieciDenari ?? 0,
        dueDenari: res?.dueDenari ?? 0,
        primiera: res?.primiera ?? 0,
        lowHand: res?.lowHand ?? 0,
        threeAces: res?.threeAces ?? 0,
        scope: res?.scope ?? 0,
        score: res?.score ?? 0,
      });
    }

    roundResults = rotatedResults;
    for (let i = 0; i < numPlayers; i++) {
      if (players[i]) {
        players[i].currentRoundScore = roundResults[i]?.score ?? 0;
      }
    }
  }

  let lastToCapture: number | undefined = undefined;
  if (match.lastToCapture !== null && match.lastToCapture !== undefined) {
    lastToCapture = (match.lastToCapture - activeSeatIndex + numPlayers) % numPlayers;
  }

  let lastPlayedCard = undefined;
  if (match.lastPlayedCard) {
    lastPlayedCard = {
      card: match.lastPlayedCard.card,
      playerIndex: (match.lastPlayedCard.playerIndex - activeSeatIndex + numPlayers) % numPlayers,
    };
  }

  return {
    deck: match.deck || [],
    table: match.table || [],
    players,
    currentPlayerIndex,
    numPlayers,
    lastToCapture,
    lastPlayedCard,
    phase,
    messages: match.messages || [],
    roundResults,
    scopaEvent: scopaEvent || undefined,
    lowHandEvent: lowHandEvent || undefined,
    roundStartPlayer: (match.roundStartPlayer - activeSeatIndex + numPlayers) % numPlayers,
    roundEndedAt: match.roundEndedAt,
  };
}
